import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import {
  MatSelect,
  MatSelectConfig,
  MAT_SELECT_CONFIG,
} from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { Breakpoints } from '@rd-lib/breakpoints';
import { DateService } from '@rd/core/services/date/date.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { AutoRedemptionDatePickerDialogComponent } from '../../components/date-picker-dialog/date-picker-dialog.component';

@Component({
  selector: 'rd-auto-redemption-date-input',
  templateUrl: './auto-redemption-date-input.component.html',
  styleUrls: ['./auto-redemption-date-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AutoRedemptionDateInputComponent,
      multi: true,
    },
    DialogService,
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        overlayPanelClass: 'ar-date-input-overlay-panel',
      } as MatSelectConfig,
    },
  ],
})
export class AutoRedemptionDateInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @HostBinding('class.rd-auto-redemption-date-input')
  componentClass = true;

  @ViewChild('datepickerTriggerButton')
  datepickerTriggerButton: ElementRef<HTMLButtonElement>;

  @ViewChild('datePickerContainer')
  datePickerContainer: MatSelect;

  minDate = this.dateService.toUTCDateWithOffset(
    this.dateService.dayjs().add(1, 'day').toDate(),
  );
  maxDate = this.dateService.toUTCDateWithOffset(
    this.dateService.dayjs().add(1, 'year').toDate(),
  );

  destroy$ = new Subject<void>();
  disabled = false;
  internalDate = this.minDate;
  datePickerFormControl = new UntypedFormControl(this.minDate);

  @Input() dateFormat: string;

  isSelectingDate = false;

  isNavigatingWithKeyboard: boolean;
  hostTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  onChange: (date: Date) => void;
  onTouched: () => void;

  constructor(
    private urlUtilsService: UrlUtilsService,
    private dateService: DateService,
    private translateService: TranslateService,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
  ) {}

  // CVA methods
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  writeValue(obj: Date): void {
    this._setValidatedDate(obj);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  ngOnInit(): void {
    this.datePickerFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap((a) => {
          // handle keyboard navigation
          if (this.isNavigatingWithKeyboard) {
            this.internalDate = a;
            this.onChange(a);
            this.cdRef.detectChanges();
          }
        }),
        tap((a) => {
          if (
            !this.datesAreEqual(
              this.internalDate,
              this.datePickerFormControl.value,
            ) &&
            !this.isNavigatingWithKeyboard
          ) {
            this.internalDate = a;
            this.onChange(a);
            this.teardownDateSelection();
          }

          this.datePickerContainer?.close();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
  }

  @HostListener('keydown', ['$event'])
  inputKeyDown(event: KeyboardEvent): void {
    const NAVIGATION_KEYS = [
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'Alt',
      'Control',
      'Shift',
    ];

    if (
      event.code === 'Esc' ||
      (event.code === 'Enter' && this.isSelectingDate)
    ) {
      event.preventDefault();
      this.isNavigatingWithKeyboard = false;
    } else if (event.code === 'Space' && !this.isSelectingDate) {
      this.isNavigatingWithKeyboard = true;
    } else {
      this.isNavigatingWithKeyboard = NAVIGATION_KEYS.includes(event.key);
    }

    if (!this.isNavigatingWithKeyboard) {
      this.teardownDateSelection();
    }
  }

  selectDate(date: Date): void {
    if (this.datesAreEqual(date, this.internalDate)) {
      return;
    }

    this.internalDate = date;
    this.onChange(date);
  }

  prepareDateSelection(): void {
    this.isSelectingDate = true;
    this.onTouched();
    this.isNavigatingWithKeyboard = false;

    if (this.isMobileViewPort()) {
      this.isSelectingDate = false;

      this.dialogService.open(AutoRedemptionDatePickerDialogComponent, {
        data: {
          inputDate: this.internalDate,
          onSubmit: (newDate: Date) => {
            this.internalDate = newDate;
            this.onChange(newDate);
            this.cdRef.detectChanges();
            this.dialogService.closeCurrentDialog();
          },
          onClose: () => {
            this.dialogService.closeCurrentDialog();
          },
        },
        id: 'order-orders-date-picker-dialog',
        screenMode: 'fullscreen',
      });
    } else {
      this.isSelectingDate = true;

      if (!this.disabled) {
        this.datePickerContainer.open();
      }
    }
  }

  teardownDateSelection(): void {
    this.isNavigatingWithKeyboard = false;
    // Focus back to the datepicker button only when user selecting before that
    if (this.isSelectingDate) {
      this.datepickerTriggerButton?.nativeElement.focus();
    }
    this.isSelectingDate = false;
    this.cdRef.detectChanges();
  }

  focusOutInputField(event: Event): void {
    event.preventDefault();
    if (!this.isNavigatingWithKeyboard) {
      this.teardownDateSelection();
    }
  }

  datesAreEqual(firstDate: Date, secondDate: Date): boolean {
    return firstDate.toDateString() === secondDate.toDateString();
  }

  /**
   * Validates and sets the date for the datepicker.
   * - If date is null/undefined or before minDate (tenant tomorrow), defaults to minDate.
   * - Updates `internalDate` and `datePickerFormControl`.
   *
   * DATE COMPARISON NOTES:
   * The comparison works because all values end up as LOCAL midnight:
   * - `dayjs('2025-12-10')` parses string as LOCAL midnight (NOT UTC!)
   * - Datepicker returns `new Date(year, month, day)` which is LOCAL midnight
   * - `minDate` (tenant tomorrow) is created via `toUTCDateWithOffset()` as LOCAL midnight
   *
   * Since all values use the same LOCAL midnight representation (representing tenant calendar dates),
   * the timestamp comparison via `.isBefore()` correctly compares calendar dates.
   */
  private _setValidatedDate(date: Date | string | null): void {
    const isInvalidOrBeforeTomorrow =
      !date || this.dateService.dayjs(date).isBefore(this.minDate);
    const validDate = isInvalidOrBeforeTomorrow
      ? this.minDate
      : this.dateService.dayjs(date).toDate();

    this.internalDate = validDate;
    this.datePickerFormControl.patchValue(validDate);
  }

  private isMobileViewPort(): boolean {
    return window.innerWidth < Breakpoints.TABLET;
  }
}
